import React from 'react';
import { rgbToHex, slugify } from '../../lib/utils';

const commonuiColors = {
  gray: [
    {
      name: 'Grey 1',
      r: 22,
      g: 32,
      b: 42,
    },
    {
      name: 'Grey 2',
      r: 37,
      g: 50,
      b: 60,
    },
    {
      name: 'Grey 3',
      r: 90,
      g: 104,
      b: 116,
    },
    {
      name: 'Grey 4',
      r: 140,
      g: 154,
      b: 162,
    },
    {
      name: 'Grey 5',
      r: 194,
      g: 202,
      b: 206,
    },
    {
      name: 'Grey 6',
      r: 235,
      g: 237,
      b: 238,
    },
    {
      name: 'Grey 7',
      r: 241,
      g: 245,
      b: 247,
    },
  ],
  blue: [
    {
      name: 'Blue 1',
      r: 8,
      g: 29,
      b: 55,
    },
    {
      name: 'Blue 2',
      r: 17,
      g: 43,
      b: 74,
    },
    {
      name: 'Blue 3',
      r: 31,
      g: 51,
      b: 116,
    },
    {
      name: 'Blue 4',
      r: 0,
      g: 110,
      b: 182,
    },
    {
      name: 'Blue 5',
      r: 37,
      g: 169,
      b: 224,
    },
    {
      name: 'Blue 6',
      r: 60,
      g: 200,
      b: 255,
    },
    {
      name: 'Blue 7',
      r: 235,
      g: 249,
      b: 255,
    },
  ],
  red: [
    {
      name: 'Red 1',
      r: 96,
      g: 0,
      b: 23,
    },
    {
      name: 'Red 2',
      r: 159,
      g: 0,
      b: 38,
    },
    {
      name: 'Red 3',
      r: 223,
      g: 0,
      b: 53,
    },
    {
      name: 'Red 4',
      r: 255,
      g: 0,
      b: 61,
    },
    {
      name: 'Red 5',
      r: 255,
      g: 226,
      b: 228,
    },
  ],
  green: [
    {
      name: 'Green 1',
      r: 14,
      g: 76,
      b: 1,
    },
    {
      name: 'Green 2',
      r: 24,
      g: 126,
      b: 2,
    },
    {
      name: 'Green 3',
      r: 33,
      g: 176,
      b: 2,
    },
    {
      name: 'Green 4',
      r: 42,
      g: 227,
      b: 3,
    },
    {
      name: 'Green 5',
      r: 193,
      g: 254,
      b: 179,
    },
  ],
  limegreen: [
    {
      name: 'Lime 1',
      r: 227,
      g: 233,
      b: 53,
    },
    {
      name: 'Lime 2',
      r: 0,
      g: 228,
      b: 115,
    },
  ],
  orange: [
    {
      name: 'Orange 1',
      r: 255,
      g: 103,
      b: 0,
    },
    {
      name: 'Orange 2',
      r: 255,
      g: 145,
      b: 46,
    },
    {
      name: 'Orange 3',
      r: 255,
      g: 169,
      b: 92,
    },
    {
      name: 'Orange 4',
      r: 252,
      g: 207,
      b: 0,
    },
  ],
  yellow: [
    {
      name: 'Yellow 1',
      r: 254,
      g: 255,
      b: 232,
    },
    {
      name: 'Yellow 2',
      r: 255,
      g: 221,
      b: 0,
    },
  ],
  purple: [
    {
      name: 'Purple 1',
      r: 71,
      g: 45,
      b: 82,
    },
    {
      name: 'Purple 2',
      r: 99,
      g: 63,
      b: 115,
    },
    {
      name: 'Purple 3',
      r: 153,
      g: 107,
      b: 174,
    },
    {
      name: 'Purple 4',
      r: 199,
      g: 173,
      b: 210,
    },
    {
      name: 'Purple 5',
      r: 221,
      g: 206,
      b: 228,
    },
  ],
  teal: [
    {
      name: 'Teal 2',
      r: 2,
      g: 122,
      b: 104,
    },
    {
      name: 'Teal 4',
      r: 8,
      g: 184,
      b: 157,
    },
    {
      name: 'Teal 5',
      r: 127,
      g: 203,
      b: 174,
    },
  ],
};

const renderColorCards = (colorArray) => {
  return colorArray.map((color, i) => {
    const { r, g, b, name } = color;
    return (
      <div className="grid__col-xlg-3 grid__col-lg-4 grid__col-md-6 grid__col-sm-12" key={ `color_${ i }` }>
        <div className="color">
          <div
            className="color__example"
            style={{ 'backgroundColor': `rgb(${ r }, ${ g }, ${ b })` }}></div>
          <div className="color__details">
            <h4 className="color__title">{name}</h4>
            <div><code>{rgbToHex(color)}</code></div>
            <div>R:{r} G:{g} B:{b}</div>
            <div><code>$commonui-color-{slugify(name)}</code></div>
          </div>
        </div>
      </div>
    );
  });
};

export default function Colors() {
  return (
    <section>
      <a className="anchor" id="colors"></a>
      <h2>Colors</h2>
      <br/>
      <p>Color plays a major role in our brand expression. Our primary palette of colors asserts our distinctiveness and differentiates us from our competitors. Also note that we apply a high level of stringency to our neutral palette. Be sure that color fidelity is strictly maintained across all communications. To ensure this consistency, use the formulas provided here. They have been carefully selected to achieve optimum results across print and digital media.</p>


      <h3 className="flush--bottom">Gray Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.gray)}
      </div>
      <br/>

      <h3 className="flush--bottom">Blue Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.blue)}
      </div>
      <br/>

      <h3 className="flush--bottom">Red Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.red)}
      </div>
      <br/>

      <h3 className="flush--bottom">Orange Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.orange)}
      </div>
      <br/>

      <h3 className="flush--bottom">Yellow Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.yellow)}
      </div>
      <br/>

      <h3 className="flush--bottom">Green Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.green)}
      </div>
      <br/>

      <h3 className="flush--bottom">Lime Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.limegreen)}
      </div>
      <br/>

      <h3 className="flush--bottom">Teal Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.teal)}
      </div>
      <br/>

      <h3 className="flush--bottom">Purple Palette</h3>
      <div className="grid">
        {renderColorCards(commonuiColors.purple)}
      </div>
      <br/>

      <hr/>

    </section>
  );
}



// WEBPACK FOOTER //
// ./styleguide/components/sections/Colors.js